import { NgModule, APP_INITIALIZER } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { LayoutModule } from '@angular/cdk/layout';
import { HashLocationStrategy, LocationStrategy } from '@angular/common';

import { AmplifyAuthenticatorModule } from '@aws-amplify/ui-angular';

import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatNativeDateModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { UnauthorizedComponent } from './views/error/unauthorized.component';
import { WhatsappConnectComponent } from './views/whatsapp-connect/whatsapp-connect.component';
import { WhatsappLinkHandlerComponent } from './views/whatsapp-connect/whatsapp-link-handler.component';
import { VerifyTenantSessionComponent } from './views/auth/verify-tenant-session.component';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { NavComponent } from './nav/nav.component';
import { TaxCalculatorDialogComponent } from './tax-calculator-dialog/tax-calculator-dialog.component';
import { AuthComponent } from './views/auth/auth.component';
import { SplashScreenComponent } from './splash-screen/splash-screen.component';
import { SplashScreenService } from './splash-screen/splash-screen.service';
import { httpInterceptorProviders } from './interceptors';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ImportComponent } from './views/products/import/import.component';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { applyAppLanguage, resolveStartupLang } from './language-bootstrap';

// Required for AOT compilation
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function appInitializerFactory(translate: TranslateService, splashScreenService: SplashScreenService) {
  return () => {
    // Show splash screen at the start of app initialization
    splashScreenService.show();
    const lang = resolveStartupLang();
    applyAppLanguage(translate, lang);

    // IMPORTANT:
    // Returning a Promise here tells Angular to WAIT for this async work to finish
    // BEFORE bootstrapping the app and rendering any components.
    // This ensures that translations are loaded and the splash screen is hidden
    // before the user sees the main UI.
    //
    // firstValueFrom is the modern RxJS way to convert an Observable to a Promise.
    return firstValueFrom(translate.use(lang)).then(() => {
      splashScreenService.hide();
    }).catch(error => {
      splashScreenService.hide();
    });
  };
}

@NgModule({
  declarations: [
    AppComponent,
    NavComponent,
    TaxCalculatorDialogComponent,
    AuthComponent,
    UnauthorizedComponent,
    SplashScreenComponent,
    WhatsappConnectComponent,
    WhatsappLinkHandlerComponent,
    VerifyTenantSessionComponent,
  ],
  imports: [
    AmplifyAuthenticatorModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    HttpClientModule,
    LayoutModule,
    MatButtonModule,
    MatCardModule,
    MatGridListModule,
    MatIconModule,
    MatListModule,
    MatMenuModule,
    MatNativeDateModule,
    MatProgressSpinnerModule,
    MatSidenavModule,
    MatToolbarModule,
    MatSnackBarModule,
    FormsModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatDatepickerModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatRadioModule,
    MatSelectModule,
    MatTableModule,
    MatTooltipModule,
    TranslateModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
  ],
  providers: [
    HttpClientModule,
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy,
    },
    httpInterceptorProviders,
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TranslateService, SplashScreenService],
      multi: true
    }
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
